// ═══════════════════════════════════════════════════════════════
// frontend/src/lib/api.ts — CloudERP API client
// Uses absolute worker URL for both production and dev
// ═══════════════════════════════════════════════════════════════

const API_BASE = "https://clouderp.qwingho.workers.dev/api/v1";

type ApiResponse<T = any> = { ok: true; data: T } | { ok: false; error: { code: string; message: string; details?: any } };

async function request<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (!headers["Authorization"]) headers["X-Dev-Auth"] = "dev";
  headers["X-Tenant-ID"] = "1";
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const body: ApiResponse<T> = await res.json();
  if (!body.ok) throw new Error(body.error?.message ?? `API error ${res.status}`);
  return body.data;
}

export const PartnerApi = {
  list: (p?: string) => request<any[]>(`/res.partner${p?`?${p}`:""}`),
  get: (id: number) => request<any>(`/res.partner/${id}`),
  create: (d: any) => request<any>("/res.partner", { method: "POST", body: JSON.stringify(d) }),
  update: (id: number, d: any) => request<any>(`/res.partner/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  delete: (id: number) => request<void>(`/res.partner/${id}`, { method: "DELETE" }),
};
export const ProductApi = {
  list: (p?: string) => request<any[]>(`/product.template${p?`?${p}`:""}`),
  get: (id: number) => request<any>(`/product.template/${id}`),
  create: (d: any) => request<any>("/product.template", { method: "POST", body: JSON.stringify(d) }),
};
export const CrmApi = {
  list: (p?: string) => request<any[]>(`/crm.lead${p?`?${p}`:""}`),
  get: (id: number) => request<any>(`/crm.lead/${id}`),
  create: (d: any) => request<any>("/crm.lead", { method: "POST", body: JSON.stringify(d) }),
  update: (id: number, d: any) => request<any>(`/crm.lead/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  pipeline: () => request<{ stageId: number; count: number; totalRevenue: number }[]>("/crm.lead/pipeline"),
  move: (id: number, stageId: number) => request<any>(`/crm.lead/${id}/move`, { method: "POST", body: JSON.stringify({ stageId }) }),
  qualify: (id: number, partnerId?: number) => request<any>(`/crm.lead/${id}/qualify`, { method: "POST", body: JSON.stringify({ partnerId }) }),
  createQuotation: (id: number) => request<any>(`/crm.lead/${id}/create-quotation`, { method: "POST" }),
};
export const SaleApi = {
  list: (p?: string) => request<any[]>(`/sale.order${p?`?${p}`:""}`),
  get: (id: number) => request<any>(`/sale.order/${id}`),
  create: (d: any) => request<any>("/sale.order", { method: "POST", body: JSON.stringify(d) }),
  update: (id: number, d: any) => request<any>(`/sale.order/${id}`, { method: "PUT", body: JSON.stringify(d) }),
  confirm: (id: number) => request<any>(`/sale.order/${id}/confirm`, { method: "POST" }),
  cancel: (id: number) => request<any>(`/sale.order/${id}/cancel`, { method: "POST" }),
  createInvoice: (id: number) => request<any>(`/sale.order/${id}/create-invoice`, { method: "POST" }),
  createDelivery: (id: number) => request<any>(`/sale.order/${id}/create-delivery`, { method: "POST" }),
  lines: (id: number) => request<any[]>(`/sale.order/${id}/lines`),
};
export const InvoiceApi = {
  list: (p?: string) => request<any[]>(`/account.move${p?`?${p}`:""}`),
  get: (id: number) => request<any>(`/account.move/${id}`),
  create: (d: any) => request<any>("/account.move", { method: "POST", body: JSON.stringify(d) }),
  post: (id: number) => request<any>(`/account.move/${id}/post`, { method: "POST" }),
  pay: (id: number) => request<any>(`/account.move/${id}/pay`, { method: "POST" }),
  lines: (id: number) => request<any[]>(`/account.move/${id}/lines`),
};
export const DeliveryApi = {
  list: (p?: string) => request<any[]>(`/stock.picking${p?`?${p}`:""}`),
  get: (id: number) => request<any>(`/stock.picking/${id}`),
  create: (d: any) => request<any>("/stock.picking", { method: "POST", body: JSON.stringify(d) }),
  confirm: (id: number) => request<any>(`/stock.picking/${id}/confirm`, { method: "POST" }),
  done: (id: number) => request<any>(`/stock.picking/${id}/done`, { method: "POST" }),
  moves: (id: number) => request<any[]>(`/stock.picking/${id}/moves`),
};
export const DashboardApi = {
  stats: async () => {
    const [partners, products, leads, orders] = await Promise.all([
      request<any[]>("/res.partner").catch(() => []),
      request<any[]>("/product.template").catch(() => []),
      request<any[]>("/crm.lead").catch(() => []),
      request<any[]>("/sale.order").catch(() => []),
    ]);
    return { totalPartners: partners.length, totalProducts: products.length, totalLeads: leads.length, totalOrders: orders.length, pipelineRevenue: leads.reduce((s,l)=>s+(l.expectedRevenue??0),0) };
  },
};
